// The numbers the Request signing section, its banner and its confirmation
// dialog all quote. One derivation for the three of them, so the figure in the
// warning is the figure on the bar — and a file of its own, so the component
// beside it stays a component.

import type { SigningStats } from "../api";

/**
 * The window's counters, reduced to what a rollout decision is made on.
 */
export interface SigningRollout {
  total: number;
  valid: number;
  /**
   * What enforce would turn into a 401 — everything but valid and the
   * fail-open. The fail-open is left out because those requests pass in
   * enforce too (§3.4): adding them in would name a number that will not
   * happen.
   */
  refused: number;
  /** Requests that were never weighed, and pass in enforce as well. */
  unchecked: number;
  /** valid / total, 0..1. */
  signedShare: number;
  /** refused / total, 0..1. */
  refusedShare: number;
}

/**
 * null whenever there is nothing to state — no answer from the store, or
 * nothing counted yet. Both are empty, and the section tells them apart itself;
 * what neither of them is, is a zero to do arithmetic on.
 */
export function signingRollout(
  stats: SigningStats | undefined,
): SigningRollout | null {
  if (!stats || !stats.available || stats.total <= 0) return null;
  const total = stats.total;
  const valid = stats.by_result.valid ?? 0;
  const unchecked = stats.by_result.store_unavailable ?? 0;
  const refused = Math.max(0, total - valid - unchecked);
  return {
    total,
    valid,
    refused,
    unchecked,
    signedShare: valid / total,
    refusedShare: refused / total,
  };
}

/**
 * The share of signed traffic below which the page starts raising its voice.
 *
 * It lives in the UI and decides two things only: the tone of the banner, and
 * whether Save asks again. It can refuse nothing — a studio picks its own
 * number, and this one is the platform's opinion, not its rule (§11.3).
 */
export const SIGNED_SHARE_THRESHOLD = 0.99;

/** Thousands separated, the way every count on this page is written. */
export function formatCount(n: number): string {
  return n.toLocaleString("en-US");
}

/** One decimal — the precision the headline claims and the banner repeats. */
export function formatShare(share: number): string {
  return `${(share * 100).toFixed(1)} %`;
}
